import { useState, useEffect, useId } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** Texto a digitar (case-insensitive, trim) para liberar a ação destrutiva. Omitir = só botão. */
  challenge?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  challenge,
  confirmLabel,
  cancelLabel,
  destructive = true,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const inputId = useId();

  useEffect(() => {
    // Reset do challenge entre aberturas — Dialog do MUI mantém o conteúdo
    // montado enquanto a app está viva, então o reset não acontece naturalmente.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!open) setTyped('');
  }, [open]);

  const challengeOk = !challenge || typed.trim().toLowerCase() === challenge.trim().toLowerCase();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      aria-labelledby={`${inputId}-title`}
      aria-describedby={`${inputId}-desc`}
    >
      <DialogTitle id={`${inputId}-title`}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id={`${inputId}-desc`} sx={{ mb: challenge ? 2 : 0 }}>
          {description}
        </DialogContentText>
        {challenge && (
          <TextField
            id={inputId}
            fullWidth
            size="small"
            label={t('components.confirmDialog.challengeLabel', { challenge })}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputProps={{
              'aria-label': t('components.confirmDialog.challengeLabel', { challenge }),
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {cancelLabel ?? t('components.confirmDialog.cancel')}
        </Button>
        <Button
          onClick={() => void onConfirm()}
          color={destructive ? 'error' : 'primary'}
          variant="contained"
          disabled={!challengeOk || loading}
        >
          {loading
            ? t('components.confirmDialog.confirming')
            : (confirmLabel ?? t('components.confirmDialog.confirm'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
